import React, { useEffect, useMemo, useState } from 'react';
import { Banner, Text } from '@dsny/dsny-component-library';
import theme from 'src/styles/theme';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'src/app/store';
import { getComparisonBadgeStyle } from 'src/utils/ComparisonBadge';
import {
  formatNumberAboveTenThousand,
  formatTextForSentryLog,
} from 'src/utils/Formatter';
import * as Sentry from '@sentry/react';
import {
  daysLeft,
  getDaysToStart,
  trackingStatus,
  validateSpinsDetection,
} from 'src/utils/VerifyTrackStatus';
import { useNavigate } from 'react-router-dom';
import { trackEvent } from 'src/utils/segmentService';
import { CLICK_EVENT } from 'src/consts/analytics_consts';
import { Close } from '@dsny/dsny-component-library/icons';
import { useStatsigClient } from '@statsig/react-bindings';
import { DELAYED_RADIO_PROCESSING } from 'src/types/FeataureFlags';
import {
  LibraryWrapper,
  DashboardHeading,
  LibraryContainer,
  LibrarydHeading,
  LibraryBox,
  TableBoxDesktop,
  TableBoxMobile,
  LibrarydHeadingWrapper,
  SearchWrapper,
  FilterByWrapper,
  StyledPill,
  InsightWelcomeMessage,
  DashboardHeadingWrapper,
  DashboardHeadingSkeleton,
  LibraryHeaderSkeletonWrapper,
  LibraryHeadingSkeleton,
  LibraryDescriptionSkeleton,
  SearchInputSkeleton,
  InsightsMobileWrapper,
  BannerWrapper,
} from './Library.styles';
import AddNewTrackMobile from '../Dashboard/AddNewTrack/AddNewTrackMobile';
import {
  getSongs,
  selectPeriod,
  selectSong,
  TrackingStatusEnum,
  updateModalStatus,
} from '../Dashboard';
import { getSessionUser } from '../Auth';
import { EnterprisePlanLimitModal } from '../Enterprise/PlanLimit/EnterprisePlanLimitModal';
import SongTableDesktop from './SongTable/SongTableDesktop';
import { songsAirPlayReport, songsLibraryOverview } from './Library.thunks';
import {
  ComparisonBadgeStyle,
  ExtendRenewStyle,
  StatusStyle,
  StatusWrapper,
} from './SongTable/SongTable.styles';
import {
  Artist,
  clearSongLibrary,
  saveTableStatusDesktop,
  SongLibraryInsights,
  SongLibrary,
  SongInsightFlags,
} from './Library.slice';
import ActionBar from './ActionBar/ActionBar';
import SongTableMobile from './SongTable/SongTableMobile';
import SearchSongArtist from './SearchSongArtist/SearchSongArtist';
import { EnterpriseExtendSongModal } from '../Enterprise/ExtendRenew/EnterpriseExtendSongModal';
import OverviewInsightsDesktop from '../SmartInsights/OverviewInsightsDesktop';
import OverviewInsightsMobile from '../SmartInsights/OverviewInsightsMobile';

export type TableSongData = {
  songId: string;
  song: string;
  artist: string;
  spins_all_count: number;
  spins_count: number;
  spins_difference: number;
  status: number;
  startTrackingOn: string;
  endTrackingOn: string;
  insightsFlags?: SongInsightFlags;
  topGenres?: string[];
  topRegions?: string[];
};

export type SortingRule = { id: string; desc: boolean };

const Library: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { client } = useStatsigClient();
  const { modalControl } = useSelector((state: RootState) => state.dashboard);
  const {
    songsLibrary,
    isFetching,
    songsLibraryInsights,
    songsLibraryInsightLists,
  } = useSelector((state: RootState) => state.library);
  const [rowSelection, setRowSelection] = useState<Record<number, boolean>>({});
  const [rowSelectionIds, setRowSelectionIds] = useState<string[]>([]);
  const [searchRowSelectionIds, setSearchRowSelectionIds] = useState<string[]>(
    []
  );
  const [tableData, setTableData] = useState<TableSongData[]>([]);
  const [sortedTableData, setSortedTableData] = useState<TableSongData[]>([]);
  const [showEnterpriseExtend, setShowEnterpriseExtend] = useState(false);
  const [noDataToExportToolTip, setNoDataToExportToolTip] =
    useState<boolean>(false);
  const [searchWord, setSearchWord] = useState<string>('');
  const [insightTempList, setInsightTempList] = useState<SongLibrary[]>([]);
  const [selectedInsightLabelKey, setSelectedInsightLabelKey] =
    useState<string>('');
  const [selectedInsightType, setSelectedInsightType] = useState<string>('');

  const { songs, expiredSongs } = useSelector(
    (state: RootState) => state.dashboard.songSelection
  );

  const { enterpriseProfile } = useSelector(
    (state: RootState) => state.dashboard
  );

  // Getting session user display name
  const { display = '' } = getSessionUser() || {};

  // Preparing insight counts with default values
  const insightCounts: SongLibraryInsights = {
    momentumInsightsGain: songsLibraryInsights?.momentumInsightsGain ?? 0,
    momentumInsightsStagnant:
      songsLibraryInsights?.momentumInsightsStagnant ?? 0,
    momentumInsightsDown: songsLibraryInsights?.momentumInsightsDown ?? 0,
    peakAirplayMorningInsights:
      songsLibraryInsights?.peakAirplayMorningInsights ?? 0,
    peakAirplayEveningInsights:
      songsLibraryInsights?.peakAirplayEveningInsights ?? 0,
    peakAirplayNightInsights:
      songsLibraryInsights?.peakAirplayNightInsights ?? 0,
    rotationInsights: songsLibraryInsights?.rotationInsights ?? 0,
    regionsInsights: songsLibraryInsights?.regionsInsights ?? 0,
    genresInsights: songsLibraryInsights?.genresInsights ?? 0,
  };

  const hasAnyInsightCount = useMemo(
    () => Object.values(insightCounts).some((count) => Number(count) > 0),
    [insightCounts]
  );

  // Getting top 3 region and genre names
  const topRegionNames = useMemo(() => {
    const regionMap = songsLibraryInsightLists?.regionsInsights ?? {};
    return Object.entries(regionMap)
      .sort(([, songsA], [, songsB]) => songsB.length - songsA.length)
      .slice(0, 3)
      .map(([regionName]) => regionName)
      .filter(Boolean);
  }, [songsLibraryInsightLists]);

  // Getting top 3 genre names
  const topGenreNames = useMemo(() => {
    const genreMap = songsLibraryInsightLists?.genresInsights ?? {};
    return Object.entries(genreMap)
      .sort(([, songsA], [, songsB]) => songsB.length - songsA.length)
      .slice(0, 3)
      .map(([genreName]) => genreName)
      .filter(Boolean);
  }, [songsLibraryInsightLists]);

  useEffect(() => {
    dispatch(getSongs());
  }, []);

  // Handling enterprise limit modal
  const handleShowEnterpriseLimit = (displayEnterpriseLimit: boolean) => {
    dispatch(updateModalStatus(displayEnterpriseLimit));
  };

  // Handling row ids
  const findRowIndexFromIds = (
    ids: string[],
    sortedTableDataSearch: { songId: string }[]
  ) => {
    const indexMap: Record<number, boolean> = {};

    sortedTableDataSearch.forEach((row, index) => {
      if (ids.includes(row.songId)) {
        indexMap[index] = true;
      }
    });

    return indexMap;
  };

  // Handling row selection
  const handleRowSelectionChange = (
    nextSelection:
      | Record<number, boolean>
      | ((selection: Record<number, boolean>) => Record<number, boolean>)
  ) => {
    setRowSelection((prevSelection) => {
      const resolvedSelection =
        typeof nextSelection === 'function'
          ? nextSelection(prevSelection)
          : nextSelection;

      const selectedRows = Object.keys(resolvedSelection).map(Number);
      const selectedTableSongs = selectedRows
        .map((row) => sortedTableData[row]?.songId)
        .filter((id): id is string => Boolean(id));

      setRowSelectionIds((prevSelected) => {
        const visibleIds = new Set(sortedTableData.map(({ songId }) => songId));
        const retainedSelections = prevSelected.filter(
          (id) => !visibleIds.has(id)
        );
        const updatedSelection = Array.from(
          new Set([...retainedSelections, ...selectedTableSongs])
        );

        setSearchRowSelectionIds(updatedSelection);
        return updatedSelection;
      });

      return resolvedSelection;
    });
  };

  useEffect(() => {
    setNoDataToExportToolTip(false);
    rowSelectionIds.forEach((rowSongId) =>
      sortedTableData.forEach((data) => {
        if (
          !validateSpinsDetection(data.spins_all_count) &&
          data.songId === rowSongId
        ) {
          setNoDataToExportToolTip(true);
        }
      })
    );
  }, [rowSelectionIds, sortedTableData]);

  useEffect(() => {
    const nextSelection = findRowIndexFromIds(rowSelectionIds, sortedTableData);
    setRowSelection(nextSelection);
  }, [rowSelectionIds, sortedTableData]);

  useEffect(() => {
    dispatch(songsLibraryOverview());
    return () => {
      // clear sortedTableData and tableData on unmount
      dispatch(clearSongLibrary());
    };
  }, []);

  // Handling dashboard (airplay report) navigation
  const handleNavigate = (id: string) => {
    navigate(`/dashboard?songId=${id}`);
  };

  // Handling extend-renew flows
  const handleRenewOrExtend = (id: string) => {
    if (!enterpriseProfile.isEnterpriseProfile) {
      navigate(`/dashboard?songId=${id}&openExtendModal=${true}`);
      return;
    }

    const fullSongList = [...(songs || []), ...(expiredSongs || [])];
    const toRenewSong = fullSongList?.filter((song) => song.song_id === id);

    if (!toRenewSong.length) {
      Sentry.captureException(
        formatTextForSentryLog(
          'failed to find song id for renew or extend modal'
        )
      );
      return;
    }

    dispatch(selectSong(toRenewSong[0]));
    dispatch(selectPeriod(0));
    setShowEnterpriseExtend(true);
  };

  // Export CSV data
  const exportCSV = (songIds: string[]) => {
    trackEvent(CLICK_EVENT, {
      type: 'library_export_csv',
      source: 'dashboard_library_page',
      song_ids: songIds.join(','),
    });
    dispatch(songsAirPlayReport(songIds));
  };

  // Getting status order
  const getStatusOrder = (songLib: SongLibrary) => {
    const endTrackingOn = songLib.tracking_periods[0]?.end_tracking_on;
    const remainingDays = daysLeft(new Date(endTrackingOn));
    return remainingDays;
  };

  // Getting insights count
  const getInsightsCount = (insightsFlags?: SongInsightFlags): number => {
    if (!insightsFlags) {
      return 0;
    }

    const hasMomentum =
      insightsFlags.hasMomentumGainInsight ||
      insightsFlags.hasMomentumStagnantInsight ||
      insightsFlags.hasMomentumDownInsight;
    const hasPeak =
      insightsFlags.hasPeakAirplayMorningInsight ||
      insightsFlags.hasPeakAirplayEveningInsight ||
      insightsFlags.hasPeakAirplayNightInsight;

    return [
      hasMomentum,
      hasPeak,
      insightsFlags.hasRotationInsight,
      insightsFlags.hasRegionsInsight,
      insightsFlags.hasGenresInsight,
    ].filter(Boolean).length;
  };

  // Building table data
  const buildTableData = (library: SongLibrary[] = []): TableSongData[] => {
    return library.map((song) => ({
      songId: song.id,
      song: song.title,
      artist: song.artists?.map((artist: Artist) => artist.name).join(', '),
      spins_all_count: song.all_count,
      spins_count: song.count,
      spins_difference: song.difference,
      status: getStatusOrder(song),
      startTrackingOn: song.tracking_periods[0].start_tracking_on,
      endTrackingOn: song.tracking_periods[0].end_tracking_on,
      insightsFlags: song.insightsFlags,
      topGenres: song.insights?.top_genres?.map((genre) => genre.name) ?? [],
      topRegions:
        song.insights?.top_regions?.map((region) => region.name) ?? [],
    }));
  };

  useEffect(() => {
    if (tableData.length === 0) {
      const tableDataTemp = buildTableData(songsLibrary ?? []);

      if (searchWord === '') {
        setTableData(tableDataTemp);
      }
    }
  }, [songsLibrary]);

  // Handling sorting - desktop
  const handleSorting = (sorting: SortingRule[], songSortSelected: boolean) => {
    const sortedData = [
      ...(sortedTableData.length ? sortedTableData : tableData ?? []),
    ];
    const sortBy = sorting[0]?.id;
    const desc = sorting[0]?.desc ?? false;

    dispatch(
      saveTableStatusDesktop({
        id: sortBy,
        order: desc ? 'DESC' : 'ASC',
        songSortSelected,
      })
    );

    switch (sortBy) {
      case 'song':
        sortedData.sort((a, b) =>
          desc ? b.song.localeCompare(a.song) : a.song.localeCompare(b.song)
        );
        break;

      case 'song_artist':
        sortedData.sort((a, b) => {
          const key = songSortSelected ? 'song' : 'artist';
          return desc
            ? b[key]?.localeCompare(a[key])
            : a[key]?.localeCompare(b[key]);
        });
        break;

      case 'artist':
        sortedData.sort((a, b) =>
          desc
            ? b.artist?.localeCompare(a.artist)
            : a.artist?.localeCompare(b.artist)
        );
        break;

      case 'spins':
        sortedData.sort((a, b) =>
          desc
            ? b.spins_all_count - a.spins_all_count
            : a.spins_all_count - b.spins_all_count
        );
        break;

      case 'status':
        sortedData.sort((a, b) =>
          desc
            ? Number(b.status) - Number(a.status)
            : Number(a.status) - Number(b.status)
        );
        break;

      case 'insights':
        sortedData.sort((a, b) => {
          const insightsCountA = getInsightsCount(a.insightsFlags);
          const insightsCountB = getInsightsCount(b.insightsFlags);

          return desc
            ? insightsCountB - insightsCountA
            : insightsCountA - insightsCountB;
        });
        break;

      default:
        // Fallback: sort by status DESC
        sortedData.sort((a, b) => a.song.localeCompare(b.song));
        sortedData.sort((a, b) => Number(b.status) - Number(a.status));
    }

    setSortedTableData(sortedData);
    const newIndexes = findRowIndexFromIds(rowSelectionIds, sortedData);
    setRowSelection(newIndexes);
  };

  // Handling search by song-artist
  const handleSearch = (searchParam: string) => {
    setSearchWord(searchParam);
    const tempSortedTableData = insightTempList.length
      ? [...buildTableData(insightTempList)]
      : [...(tableData ?? [])];

    const filteredData = tempSortedTableData.filter(
      (item) =>
        item?.artist?.toLowerCase().includes(searchParam.toLowerCase()) ||
        item?.song?.toLowerCase().includes(searchParam.toLowerCase())
    );

    setSortedTableData(filteredData);
    const newIndexes = findRowIndexFromIds(rowSelectionIds, filteredData);
    setRowSelection(newIndexes);
  };

  // Handling insight click
  const handleInsightClick = (insightType: string, detail?: string) => {
    const getInsightLabelKey = (type: string) => {
      const insightLabelKeyMap: Record<string, string> = {
        momentum_gain: 'LIBRARY_FILTER_MOMENTUM_GAINING',
        momentum_stagnant: 'LIBRARY_FILTER_MOMENTUM_STAGNANT',
        momentum_slowing: 'LIBRARY_FILTER_MOMENTUM_LOSING',
        peak_morning: 'LIBRARY_FILTER_PEAK_AIRPLAY_MORNING',
        peak_evening: 'LIBRARY_FILTER_PEAK_AIRPLAY_EVENING',
        peak_night: 'LIBRARY_FILTER_PEAK_AIRPLAY_NIGHT',
        rotation: 'ROTATION_LABEL',
        region: 'REGION_LABEL',
        genre: 'GENRE_LABEL',
      };

      return insightLabelKeyMap[type] ?? '';
    };

    // Replace with real navigation/selection when insights are wired
    const insightMap: Record<string, SongLibrary[] | undefined> = {
      momentum_gain: songsLibraryInsightLists?.momentumInsightsGain,
      momentum_stagnant: songsLibraryInsightLists?.momentumInsightsStagnant,
      momentum_slowing: songsLibraryInsightLists?.momentumInsightsDown,
      peak_morning: songsLibraryInsightLists?.peakAirplayMorningInsights,
      peak_evening: songsLibraryInsightLists?.peakAirplayEveningInsights,
      peak_night: songsLibraryInsightLists?.peakAirplayNightInsights,
      rotation: songsLibraryInsightLists?.rotationInsights,
      region: Object.values(
        songsLibraryInsightLists?.regionsInsights ?? {}
      ).flat(),
      genre: Object.values(
        songsLibraryInsightLists?.genresInsights ?? {}
      ).flat(),
    };
    let selectedList = insightMap[insightType] ?? [];
    const selectedLabelKey =
      (insightType === 'genre' || insightType === 'region') && detail
        ? detail
        : getInsightLabelKey(insightType);

    if (insightType === 'genre' && detail) {
      selectedList =
        songsLibraryInsightLists?.genresInsights?.[detail] ?? selectedList;
    } else if (insightType === 'region' && detail) {
      selectedList =
        songsLibraryInsightLists?.regionsInsights?.[detail] ?? selectedList;
    }
    if (selectedInsightLabelKey === selectedLabelKey) {
      setInsightTempList([]);
      setSelectedInsightLabelKey('');
      setSelectedInsightType('');
      return;
    }
    setInsightTempList(selectedList);
    setSelectedInsightLabelKey(selectedLabelKey);
    setSelectedInsightType(insightType);
  };

  useEffect(() => {
    handleSearch('');
  }, [insightTempList]);

  // Get song track status
  const getTrackStatus = (
    startTrackingOn: string,
    endTrackingOn: string,
    songId: string
  ) => {
    const remainingDays = daysLeft(new Date(endTrackingOn));
    const status = trackingStatus(remainingDays);
    const daysToStart = getDaysToStart(startTrackingOn);

    /* Scheduled: Starting in X day(s) */
    if (daysToStart > 0)
      return (
        <StatusStyle>
          <Trans
            i18nKey="LIBRARY_TABLE_STATUS_SCHEDULE"
            values={{ daysToStart }}
          />
        </StatusStyle>
      );

    /* Expired (within 90 day): Extend */
    if (status === TrackingStatusEnum.EXPIRED) {
      return (
        <StatusWrapper onClick={() => handleRenewOrExtend(songId)}>
          <ExtendRenewStyle>
            <Trans i18nKey="LIBRARY_TABLE_STATUS_EXTEND" />
          </ExtendRenewStyle>
        </StatusWrapper>
      );
    }

    /* Expired (past 90 day): Renew */
    if (status === TrackingStatusEnum.PAST_EXPIRED) {
      return (
        <StatusWrapper onClick={() => handleRenewOrExtend(songId)}>
          <ExtendRenewStyle>
            <Trans i18nKey="LIBRARY_TABLE_STATUS_RENEW" />
          </ExtendRenewStyle>
        </StatusWrapper>
      );
    }

    /* Active: X days remaining */
    return (
      <StatusStyle>
        <Trans
          i18nKey="LIBRARY_TABLE_STATUS_ACTIVE"
          values={{ remainingDays }}
        />
      </StatusStyle>
    );
  };

  // Determine whether to display insights section
  const displayInsights = () => {
    if (hasAnyInsightCount || isFetching) {
      return true;
    }
    return false;
  };

  // Get comparison badge
  const getComparisonBadge = (spinsDifference: number) => {
    const { color, backgroundColor } = getComparisonBadgeStyle(spinsDifference);

    return (
      <ComparisonBadgeStyle color={color} backgroundColor={backgroundColor}>
        {formatNumberAboveTenThousand(spinsDifference, true)}
      </ComparisonBadgeStyle>
    );
  };

  // Processing spins banner message
  const processingSpinsMessage = {
    title: t('DASHBOARD_PROCESSING_SPINS_TITLE'),
    description: t('DASHBOARD_PROCESSING_SPINS_DESC'),
  };

  return (
    <>
      {/* Song library */}
      <LibraryWrapper aria-busy={isFetching}>
        <LibraryContainer>
          {/* Banner message */}
          {client.checkGate(DELAYED_RADIO_PROCESSING) && (
            <BannerWrapper>
              <Banner
                bannerContent={processingSpinsMessage}
                variant="warning"
                width="100%"
              />
            </BannerWrapper>
          )}
          {/* Add new track button - mobile */}
          <AddNewTrackMobile
            setShowEnterpriseLimit={handleShowEnterpriseLimit}
          />

          {/* Dashboard heading */}
          <DashboardHeadingWrapper>
            {isFetching ? (
              <DashboardHeadingSkeleton aria-hidden />
            ) : (
              <>
                <DashboardHeading>
                  {t('LIBRARY_GREETING', { name: display })}
                </DashboardHeading>
                {/* Welcome message */}
                {hasAnyInsightCount && (
                  <InsightWelcomeMessage>
                    {t('LIBRARY_INSIGHT_WELCOME')}
                  </InsightWelcomeMessage>
                )}
              </>
            )}
          </DashboardHeadingWrapper>

          {displayInsights() && (
            <>
              {/* Smart Insights - desktop */}
              <OverviewInsightsDesktop
                isLoading={isFetching}
                insightCounts={insightCounts}
                topRegions={topRegionNames}
                topGenres={topGenreNames}
                onInsightClick={handleInsightClick}
                selectedInsightType={selectedInsightType}
              />
              {/* Smart Insights - mobile */}
              <InsightsMobileWrapper>
                <OverviewInsightsMobile
                  isLoading={isFetching}
                  insightCounts={insightCounts}
                  topRegions={topRegionNames}
                  topGenres={topGenreNames}
                  onInsightClick={handleInsightClick}
                  selectedInsightType={selectedInsightType}
                />
              </InsightsMobileWrapper>
            </>
          )}

          <LibraryBox>
            <LibrarydHeadingWrapper>
              {isFetching ? (
                <LibraryHeaderSkeletonWrapper aria-hidden>
                  <LibraryHeadingSkeleton />
                  <LibraryDescriptionSkeleton />
                  <SearchWrapper>
                    <SearchInputSkeleton />
                  </SearchWrapper>
                </LibraryHeaderSkeletonWrapper>
              ) : (
                <>
                  {/* Library heading */}
                  <LibrarydHeading>{t('LIBRARY_HEADING')}</LibrarydHeading>

                  {/* Library description */}
                  <Text fontSize="14px" color={theme.colors.neutralW20}>
                    {t('LIBRARY_DESCRIPTION')}
                  </Text>

                  {/* Handling search by song tile/artist name */}
                  <SearchWrapper>
                    <SearchSongArtist handleSearch={handleSearch} />

                    {/* Filter selection */}
                    {insightTempList.length > 0 && (
                      <FilterByWrapper>
                        {t('LIBRARY_FILTER_BY_LABEL')}
                        <StyledPill
                          label={t(
                            selectedInsightLabelKey || 'LIBRARY_FILTER_MOMENTUM'
                          )}
                          icon={<Close />}
                          variant="outlined"
                          iconPosition="tailing"
                          onClick={() => {
                            setInsightTempList([]);
                            setSelectedInsightLabelKey('');
                            setSelectedInsightType('');
                          }}
                        />
                      </FilterByWrapper>
                    )}
                  </SearchWrapper>
                </>
              )}
            </LibrarydHeadingWrapper>

            {/* Table component - desktop */}
            <TableBoxDesktop>
              <SongTableDesktop
                songsLibrary={songsLibrary}
                rowSelection={rowSelection}
                tableData={tableData}
                sortedTableData={sortedTableData}
                isFetching={isFetching}
                getComparisonBadge={getComparisonBadge}
                getTrackStatus={getTrackStatus}
                handleNavigate={handleNavigate}
                setRowSelection={handleRowSelectionChange}
                handleSorting={handleSorting}
                exportCSV={exportCSV}
              />
            </TableBoxDesktop>

            {/* Table component - mobile */}
            <TableBoxMobile>
              <SongTableMobile
                songsLibrary={songsLibrary}
                rowSelection={rowSelection}
                tableData={tableData}
                sortedTableData={sortedTableData}
                isFetching={isFetching}
                getComparisonBadge={getComparisonBadge}
                getTrackStatus={getTrackStatus}
                handleNavigate={handleNavigate}
                setRowSelection={handleRowSelectionChange}
                handleSorting={handleSorting}
                exportCSV={exportCSV}
              />
            </TableBoxMobile>
          </LibraryBox>
        </LibraryContainer>
      </LibraryWrapper>

      {/* Action bar */}
      <ActionBar
        selectedRows={
          searchWord
            ? Array.from(
                new Set([...searchRowSelectionIds, ...rowSelectionIds])
              )
            : rowSelectionIds
        }
        isAllItemsSelected={rowSelectionIds.length === sortedTableData.length}
        noDataToExportToolTip={noDataToExportToolTip}
        exportCSV={exportCSV}
        setRowSelection={handleRowSelectionChange}
        setSearchRowSelectionIds={setSearchRowSelectionIds}
        setRowSelectionIds={setRowSelectionIds}
      />

      {/* Enterprise plan limit modal */}
      <EnterprisePlanLimitModal
        showModal={modalControl.showEnterpriseModal}
        setShowModal={handleShowEnterpriseLimit}
      />

      {/* Enterprise extend-renew modal */}
      <EnterpriseExtendSongModal
        showModal={showEnterpriseExtend}
        setShowModal={setShowEnterpriseExtend}
        isLibrary
      />
    </>
  );
};

export default Library;
